import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAnalytics } from '@/hooks/use-analytics';
import { useAppointments } from '@/hooks/use-appointments';
import { useUsers } from '@/hooks/use-users';

export const Route = createFileRoute('/analytics')({
  component: () => (
    <ProtectedRoute>
      <AnalyticsPage />
    </ProtectedRoute>
  ),
});

function AnalyticsPage() {
  const { t } = useTranslation();
  const { data: analytics, isLoading: isLoadingAnalytics } = useAnalytics();
  const { data: appointmentsData, isLoading: isLoadingAppointments } = useAppointments();
  const { data: usersData, isLoading: isLoadingUsers } = useUsers();

  // Calculate driver statistics from real data
  const driverData =
    usersData && appointmentsData
      ? Object.values(usersData.users)
          .map((user) => {
            const userAppointments = appointmentsData.appointments.filter(
              (appointment) => appointment.assignedTo === user.id,
            );
            return {
              name: user.name || user.email || t('common.unknown'),
              appointments: userAppointments.length,
            };
          })
          .filter((driver) => driver.appointments > 0)
          .sort((a, b) => b.appointments - a.appointments)
      : [];

  // Calculate weekly data from real appointments
  const weeklyData = appointmentsData
    ? (() => {
        const weeks = new Map<string, number>();
        const now = new Date();

        // Get last 4 weeks
        for (let i = 3; i >= 0; i--) {
          const weekStart = new Date(now);
          weekStart.setDate(now.getDate() - i * 7);
          weekStart.setHours(0, 0, 0, 0);
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          weekEnd.setHours(23, 59, 59, 999);

          const weekLabel = t('analytics.weekLabel', { number: 4 - i });
          const appointmentsInWeek = appointmentsData.appointments.filter((appointment) => {
            const appointmentDate = new Date(appointment.startTime);
            return appointmentDate >= weekStart && appointmentDate <= weekEnd;
          }).length;

          weeks.set(weekLabel, appointmentsInWeek);
        }

        return Array.from(weeks.entries()).map(([week, appointments]) => ({ week, appointments }));
      })()
    : [];

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">{t('analytics.title')}</h1>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('analytics.totalAppointments')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{isLoadingAnalytics ? '...' : analytics?.totalAppointments || 0}</div>
            <p className="text-xs text-muted-foreground">{t('common.allTime')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('analytics.appointmentsThisWeek')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoadingAnalytics ? '...' : analytics?.appointmentsThisWeek || 0}
            </div>
            <p className="text-xs text-muted-foreground">{t('common.thisWeek')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('analytics.assignmentRate')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoadingAnalytics ? '...' : `${analytics?.assignmentRate || 0}%`}
            </div>
            <p className="text-xs text-muted-foreground">{t('common.last30Days')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('analytics.upcomingUnassigned')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{isLoadingAnalytics ? '...' : analytics?.upcomingUnassigned || 0}</div>
            <p className="text-xs text-muted-foreground">{t('common.needDrivers')}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('analytics.driverStats')}</CardTitle>
            <CardDescription>{t('analytics.driverStatsDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingUsers || isLoadingAppointments ? (
              <div className="text-center p-4 text-muted-foreground">{t('common.loading')}</div>
            ) : !driverData.length ? (
              <div className="text-center p-4 text-muted-foreground">{t('analytics.noDriverData')}</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={driverData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="appointments" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('analytics.weeklyOverview')}</CardTitle>
            <CardDescription>{t('analytics.weeklyOverviewDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingAppointments ? (
              <div className="text-center p-4 text-muted-foreground">{t('common.loading')}</div>
            ) : !weeklyData.length ? (
              <div className="text-center p-4 text-muted-foreground">{t('analytics.noWeeklyData')}</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={weeklyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="appointments" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
